import React, { useState, useEffect } from "react";

// Libraries
import PropTypes from "prop-types";
import { isEmpty, get, some, filter } from "lodash";

// Socket
import { socket } from "@Socket";

// Redux
import { connect } from "react-redux";
import { sendMessage } from "@Redux/Actions/chats";
import { setDraft } from "@Redux/Actions/draft";

// Context
import { useListRef } from "@Features/Monitor/Context";

// Components
import IkTable from "@Components/IkTable";
import IKModal from "@Components/IkModal";

// Utils
import dataHandler from "@Utils/dataHandler";
import { messageBuilder } from "@Utils/functionality";
import { replaceVars } from "../../Constants";

// Styles
import styles from "./styles.module.css";

const QuickAnswers = ({ onClose, dispatch, currentChat, userName, search = "", settings, onSuccessSend }) => {
	const [quickAnswers, setQuickAnswers] = useState([]);
	const [checkAllData, setCheckAllData] = useState([]);
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(null);
	const { listRef } = useListRef();
	const suggestQA = get(settings, "suggestQA", "");

	// Columnas base
	const baseColumns = [
		{
			label: "ID",
			key: "id",
			format: "string",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Hotkey",
			key: "hotkey",
			format: "string",
			width: 2,
		},
		{
			label: "Título",
			key: "name",
			format: "string",
			width: 4,
		},
		{
			label: "Texto",
			key: "text",
			format: "truncate",
			stringLength: 60,
			width: 7,
		},
		{
			label: "Usos",
			key: "uses",
			dataPosition: "center",
			format: "blankZero",
			width: 1,
		},
		{
			label: "Adjunto",
			key: "uri",
			dataPosition: "center",
			format: "boolean",
			boolLabels: { true: "Si" },
			width: 1,
		},
	];

	const columns =
		suggestQA === "Múltiple"
			? [
					{
						multiple: true,
						key: "id",
						dataPosition: "center",
					},
					...baseColumns,
			  ]
			: baseColumns;

	let defaultParams = { page: 1, fields: "id,name,hotkey,text,type,uri", rows: 15 };
	if (currentChat.groupId) {
		defaultParams = { ...defaultParams, groupId: currentChat.groupId };
	}
	if (!isEmpty(search)) {
		defaultParams = { ...defaultParams, all: search };
	}

	useEffect(() => {
		(async () => {
			const quickAnswerPromise = await dataHandler({
				dispatch,
				url: "quickAnswers/grouped",
				params: defaultParams,
			});
			setFilteredCount(quickAnswerPromise.count);
			setFilteredItems(quickAnswerPromise.items || []);
		})();
	}, [search]);

	// save handlers
	const handlerSaved = async (item = null) => {
		let allSuccess = true;
		if (suggestQA === "Múltiple") {
			for (const quickAnswer of quickAnswers) {
				const text = replaceVars(quickAnswer.text, {
					usuario: userName,
					contacto: get(currentChat, "contact.name", ""),
				});

				const newMessage = messageBuilder({
					chat: currentChat,
					userName,
					text,
					type: quickAnswer.type,
					uri: quickAnswer.uri,
				});
				const result = await dispatch(sendMessage({ message: newMessage, listRef }));
				if (!result) allSuccess = false;
				else socket.emit("qa", quickAnswer.id);
			}
		} else if (suggestQA === "Sugerir") {
			const answer = item || (!isEmpty(quickAnswers) ? quickAnswers[0] : null);
			if (answer) {
				socket.emit("qa", answer.id);
				dispatch(setDraft(answer));
			}
		} else if (suggestQA === "Individual") {
			const answer = item || (!isEmpty(quickAnswers) ? quickAnswers[0] : null);
			if (answer) {
				const text = replaceVars(answer.text, {
					usuario: userName,
					contacto: get(currentChat, "contact.name", ""),
				});

				const newMessage = messageBuilder({
					chat: currentChat,
					userName,
					text,
					type: answer.type,
					uri: answer.uri,
				});
				const result = await dispatch(sendMessage({ message: newMessage, listRef }));
				if (!result) allSuccess = false;
				else socket.emit("qa", answer.id);
			}
		}

		if (allSuccess) {
			if (onSuccessSend) onSuccessSend();
			onClose();
		}
	};

	// Table Handlers
	const handlerData = async (params, _, findAll) => {
		const data = await dataHandler({
			dispatch,
			url: "quickAnswers/grouped",
			params: { ...params },
		});
		if (findAll) {
			let ungrouped = [];
			for (const group of data.items) {
				ungrouped = [...ungrouped, ...group.tasks];
			}
			setCheckAllData(ungrouped);
		} else {
			setFilteredItems(data.items);
			setFilteredCount(data.count);
			setCheckAllData([]);
		}
	};

	const handleButtom = (name, item) => {
		switch (name) {
			case "Watch":
				if (suggestQA === "Individual") {
					const text = replaceVars(item.text, {
						usuario: userName,
						contacto: get(currentChat, "contact.name", ""),
					});

					const newMessage = messageBuilder({
						chat: currentChat,
						userName,
						text,
						type: item.type,
						uri: item.uri,
					});
					dispatch(sendMessage({ message: newMessage, listRef })).then((result) => {
						if (result) {
							if (onSuccessSend) onSuccessSend();
							onClose();
						}
					});
				} else {
					handlerSaved(item);
				}
				break;
			case "onClick": {
				if (suggestQA === "Múltiple") {
					const isSelected = some(quickAnswers, (qa) => {
						return qa.id === item.id;
					});
					let newSelected;
					if (isSelected) {
						newSelected = filter(quickAnswers, (qa) => {
							return qa.id !== item.id;
						});
					} else {
						newSelected = [...quickAnswers, item];
					}
					setQuickAnswers(newSelected);
					setCheckAllData(newSelected);
				} else {
					setQuickAnswers([item]);
				}
				break;
			}

			default:
				break;
		}
	};

	const handleMultiple = (data) => {
		setQuickAnswers(data);
	};

	return (
		<IKModal
			title="Respuestas Rápidas"
			// id={tableValue.id}
			// mode={modalOption}
			// setMode={setModalOption}
			onClose={onClose}
			saveLabel="Enviar"
			saveDisabled={isEmpty(quickAnswers)}
			onSuccess={handlerSaved}
			fullHeight
			size="small">
			<div className={styles.grid}>
				<IkTable
					data={filteredItems}
					columns={columns}
					totalRows={filteredCount}
					defaultParams={defaultParams}
					handlerTable={handleButtom}
					handlerMultiple={suggestQA === "Múltiple" ? handleMultiple : undefined}
					dataHandler={handlerData}
					mergeConfig={{ groupKey: "groupName", itemsKey: "tasks", idKey: "id" }}
					checkAllData={checkAllData}
					mobileCardView
					cardLine1={["id", "hotkey", "uri"]}
					cardLine2={[{ key: "text", label: "" }]}
					searcher={{
						searchString: search,
						searchable: true,
						searchPosition: "flex-start",
						searchDisabled: false,
						hideOnMobile: true,
						width: "40rem",
					}}
					headerDetail={{ headerTitle: "groupName", dataDetail: "tasks", columnName: "categoria" }}
				/>
			</div>
		</IKModal>
	);
};

QuickAnswers.propTypes = {
	onClose: PropTypes.func,
	dispatch: PropTypes.func,
	currentChat: PropTypes.object,
	userName: PropTypes.string,
	search: PropTypes.string,
	settings: PropTypes.object,
	onSuccessSend: PropTypes.func,
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		settings: company.settings,
	};
}

export default connect(mapStateToProps)(QuickAnswers);
